import { useMemo } from "react";
import PropTypes from "prop-types";
import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useCarContext } from "../../../contexts/CarContext";
import useCarPricingMode from "../hooks/useCarPricingMode";
import CustomPagination from "../../../components/custom-component/CustomPagination";
import { MobileFilterBar, useTableFilters } from "../../../components/data-table";
import GenerateQR from "./generateQR/GenerateQR";
import CarListCards from "./CarListCards";

function CarListPanel({ onAddCarClick, onDeleteCar, addCarLoading = false }) {
    const { t } = useTranslation("cars");
    const history = useHistory();
    const isCarPricingMode = useCarPricingMode();
    const {
        cars,
        loading,
        activePageNumber,
        totalItemCount,
        pageSize,
        searchParams,
        updateSearchParams,
        updateActivePage,
        setPageSize,
    } = useCarContext();

    // Same filter state as the desktop table (it lives in CarContext), so search/reset carry over.
    const table = useTableFilters({ value: searchParams, onChange: updateSearchParams });

    const carActionItems = useMemo(
        () => [
            { label: t("view"), key: "view" },
            { label: t("rates.update"), key: "update" },
            { label: t("delete"), key: "delete", danger: true },
        ],
        [t],
    );

    const handleActionClick = (key, car) => {
        if (key === "view") {
            history.push(`/cardetails/${car?.VIN || ""}`);
            return;
        }
        if (key === "update") {
            history.push(`/cardetails/${car?.VIN || ""}`, { edit: true });
            return;
        }
        if (key === "delete") {
            onDeleteCar?.(car);
        }
    };

    const handlePageSizeChange = (size) => {
        if (size === pageSize) {
            return;
        }
        setPageSize(size);
        updateActivePage(1);
    };

    return (
        <div className="flex min-w-0 flex-col gap-4">
            <h1 className="text-lg font-bold text-[#2F2F46]">
                {t("cars")}
                <span className="text-[#5D5DF6]"> ({totalItemCount})</span>
            </h1>

            <MobileFilterBar
                filters={table}
                search={{
                    fields: [
                        { key: "carName", label: t("carName") },
                        { key: "VIN", label: t("vinNumberShort") },
                        { key: "carNumber", label: t("plateNumber") },
                        { key: "categoryName", label: t("category") },
                    ],
                    placeholder: t("mobileList.searchPlaceholder"),
                }}
                filterLabels={{
                    location: t("location"),
                    hasActivePricing: t("rate"),
                    "insuranceVehicleInfo.active": t("carDetail.insurance"),
                }}
            />

            <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    loading={addCarLoading}
                    disabled={addCarLoading}
                    onClick={() => onAddCarClick?.()}
                    className="!h-11 w-full !rounded-xl !font-medium sm:flex-1">
                    {t("addCarBtn")}
                </Button>
                <div className="w-full sm:flex-1 [&_.ant-btn]:!h-11 [&_.ant-btn]:!w-full [&_.ant-btn]:!rounded-xl">
                    <GenerateQR />
                </div>
            </div>

            <CarListCards
                cars={cars}
                loading={loading}
                t={t}
                isCarPricingMode={isCarPricingMode}
                actionItems={carActionItems}
                onActionClick={handleActionClick}
            />

            <div className="flex min-w-0 w-full justify-end">
                <CustomPagination
                    variant="spaced"
                    totalItem={totalItemCount}
                    pageSize={pageSize}
                    setPageSize={handlePageSizeChange}
                    currentPage={activePageNumber}
                    setCurrentPage={updateActivePage}
                    pageSizeOptions={[10, 20, 50, 100]}
                    showSizeChanger
                />
            </div>
        </div>
    );
}

CarListPanel.propTypes = {
    onAddCarClick: PropTypes.func,
    onDeleteCar: PropTypes.func,
    addCarLoading: PropTypes.bool,
};

export default CarListPanel;
